import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, MessageCircle, Clock, HeartCrack, Users, Moon, AlertCircle, Frown } from 'lucide-react';
export function PainAgitation() {
  const pains = [{
    icon: <Frown className="w-5 h-5 text-[#1B3A6B]" data-id="element-265" />,
    bg: 'bg-[#EEF2F7]',
    text: 'Llegas a su cuarto y no sabes qué hacer con tus manos'
  }, {
    icon: <MessageCircle className="w-5 h-5 text-[#B83A52]" data-id="element-266" />,
    bg: 'bg-[#FDF0F3]',
    text: 'Tienes miedo de decir algo incorrecto y lastimarlo más'
  }, {
    icon: <HeartCrack className="w-5 h-5 text-[#1B3A6B]" data-id="element-267" />,
    bg: 'bg-[#EEF2F7]',
    text: 'Mandas mensajes de ánimo que se sienten vacíos mientras los escribes'
  }, {
    icon: <Moon className="w-5 h-5 text-[#4A4A6A]" data-id="element-268" />,
    bg: 'bg-[#F3F4F6]',
    text: 'Te despiertas a las 3am preguntándote si estás haciendo suficiente'
  }, {
    icon: <Users className="w-5 h-5 text-[#B83A52]" data-id="element-269" />,
    bg: 'bg-[#FDF0F3]',
    text: 'Los demás familiares no ayudan igual y terminas cargando todo tú'
  }, {
    icon: <AlertCircle className="w-5 h-5 text-[#1B3A6B]" data-id="element-270" />,
    bg: 'bg-[#EEF2F7]',
    text: 'Él/ella dice "estoy bien" pero ves el miedo en sus ojos'
  }];
  return <section className="py-16 px-4 bg-[#EEF2F7] text-center border-t border-[#E2E8F0]" data-id="element-271">
      <div className="max-w-[800px] mx-auto" data-id="element-272">
        {/* Emotional Opening Questions */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-12 text-left space-y-6" data-id="element-273">
          <p className="text-[#4A4A6A] text-base md:text-lg italic font-serif" data-id="element-274">
            Permíteme preguntarte algo que nadie más se atreve a preguntarte:
          </p>
          {['¿Cuándo fue la última vez que entraste a su cuarto y supiste exactamente qué decir?', '¿Cuándo fue la última vez que saliste de visitarlo sin ese peso en el pecho?', '¿Cuándo fue la última vez que no lloraste en el carro de regreso a casa?'].map((q, i) => <div key={i} className="flex items-start gap-4 bg-white p-4 rounded-lg shadow-sm border border-[#E2E8F0]" data-id="element-275">
              <span className="text-[#B83A52] font-bold text-lg shrink-0 mt-0.5" data-id="element-276">
                →
              </span>
              <p className="text-[#1C1C2E] font-medium text-base md:text-lg leading-snug" data-id="element-277">
                {q}
              </p>
            </div>)}
        </motion.div>

        {/* "No te están preparando" block */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-12 bg-[#1B3A6B] text-white rounded-xl p-8 text-left shadow-navy-medium relative overflow-hidden" data-id="element-278">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-279"></div>

          <p className="text-[#EEF2F7] text-base mb-6 leading-relaxed" data-id="element-280">
            Cada pastilla que toma tiene un protocolo exacto. Cada tratamiento
            tiene un plan médico detallado.
            <br data-id="element-281" />
            <br data-id="element-282" />
            Pero nadie le dio a{' '}
            <span className="font-bold text-[#FCD34D]" data-id="element-283">TU FAMILIA</span> un
            protocolo para acompañarlo.
            <br data-id="element-284" />
            <br data-id="element-285" />Y sin protocolo, el amor más grande del mundo se convierte en
            caos, agotamiento y culpa.
          </p>
          <div className="border-t border-white/20 pt-6" data-id="element-286">
            <p className="text-xl md:text-2xl font-serif font-bold text-[#FDF0F3]" data-id="element-287">
              "No te están preparando. Te están dejando improvisar."
            </p>
          </div>
        </motion.div>

        {/* Pain Points Grid */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="mb-12" data-id="element-288">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#1C1C2E] mb-10" data-id="element-289">
            ¿Te suena familiar alguna de estas situaciones?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left" data-id="element-290">
            {pains.map((pain, i) => <div key={i} className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-sm flex items-start gap-4 hover:border-[#1B3A6B]/30 transition-colors" data-id="element-291">
                <div className={`p-2.5 rounded-full shrink-0 ${pain.bg}`} data-id="element-292">
                  {pain.icon}
                </div>
                <p className="text-[#4A4A6A] font-medium text-sm leading-snug" data-id="element-293">
                  {pain.text}
                </p>
              </div>)}
          </div>
        </motion.div>

        {/* Bridge Line */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center space-y-4" data-id="element-294">
          <p className="text-[#4A4A6A] text-sm md:text-base font-medium" data-id="element-295">
            No es tu culpa. Nadie te enseñó a amar a alguien con cáncer.
          </p>
          <p className="text-xl md:text-2xl font-serif font-bold text-[#B83A52]" data-id="element-296">
            Eso está a punto de cambiar.
          </p>
          <div className="flex justify-center pt-4" data-id="element-297">
            <ArrowDown className="w-5 h-5 text-[#B83A52] animate-bounce" data-id="element-298" />
          </div>
        </motion.div>
      </div>
    </section>;
}